import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import {
  CellEditingStartedEvent,
  CellEditingStoppedEvent,
  ColDef,
  ColGroupDef,
  Column,
  ColumnState,
  GridApi,
  GridOptions,
  IsGroupOpenByDefaultParams,
  ModelUpdatedEvent,
  IRowNode,
  RowNode,
  GetContextMenuItems
} from 'ag-grid-community'
import styled from '@emotion/styled'
import { debounce, isEqual, isNil, uniq } from 'lodash-es'
import { UserPreference, DefaultHeading } from 'api'
import { GridActions, GridColumnSettings, GridId, GridSettings, GridSettingsId, GridToolbarSettings } from 'store/grid'
import { DataGridToolbar, DataGridToolbarContainer } from './toolbar/DataGridToolbar'
import { ColumnSelector } from './toolbar/ColumnSelector'
import { PageControlsToolbar } from './toolbar/PageControlsToolbar'
import { AgGridWrapper } from './AgGridWrapper'
import { ModalConsumerProps, NotificationType, withModal } from 'view/shared/modal'
import { flattenColumnDefs, GroupRowData, gridEventsLogger, setNodesSelected, StandardGridColumnId } from './gridUtils'
import { RowDataGetter } from 'view/shared/datagrid'
import { SelectionActions } from 'store/selection'
import { bestPriceTypeSelector, createSelectedGridSettingsSelector } from 'selectors/gridSelectors'
import { createGridColumnAutoSizedSelector, gridRowIdInEditSelector, gridRowInEditSelector } from 'selectors/selectionSelectors'
import { userPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { Locale, Translation } from 'locale'
import { BestPriceType, colDefWithIdAndSort, CustomToolbarComponentProps } from 'store/grid/gridTypes'
import { SubscriptionDetail } from 'store/selection/selectionTypes'
import { DefaultClipboardProcessor, IClipboardProcessor } from './ClipboardProcessor'
import { getAllVisibleLeafNodes } from './getVisibleLeafNodes'
import { DataGridContext } from './DataGridContext'
import { stopPastePropagation } from 'utils'
import { ApplyTransactionGetId } from './createRowDataTransaction'
import { isNotEmpty } from 'utils/predicate'

export interface OwnProps<D = any> extends ModalConsumerProps {
  debug?: boolean
  getContextMenuItems?: GetContextMenuItems<D>
  gridId: GridId
  applyTransactionGetId?: ApplyTransactionGetId
  gridOptions: GridOptions
  'data-test-id'?: string

  gridApiRef?: React.MutableRefObject<GridApi | undefined>
  rowData: D[]
  groupRowData?: GroupRowData<any>
  columnDefs: (ColGroupDef | ColDef)[]
  placeholder?: React.ComponentType<any>
  gridClass?: string
  disableGrouping?: boolean
  disableFilters?: boolean
  saveSelectedItemIds?: boolean
  enforcedGroupingColStates?: { colId: string; sort?: 'asc' | 'desc' }[]
  disableExport?: boolean
  GroupByToggles?: React.ComponentType<{
    gridApi: GridApi | null
    rowData?: any
  }>
  clipboardProcessor?: IClipboardProcessor
  customToolbarButtons?: React.ComponentType<{
    gridApi: GridApi | null
    toolbarSettings: GridToolbarSettings
    onToolbarSettingsChanged: (toolbarSettings: GridToolbarSettings) => unknown
  }>[]
  customToolbarComponents?: React.ComponentType<CustomToolbarComponentProps>[]
  customToolbarSwitches?: React.ComponentType<any>[]
  subscriptionFilters?: (gridApi: GridApi) => JSX.Element
  subscriptionDetail?: SubscriptionDetail
  gridColumnAutoSizing?: GridAutoSizing

  trackGridRowInEdit?: boolean
  groupDefaultExpanded?: number
  defaultGroupSortComparator?: (groupAItems: any[], groupBItems: any[]) => number
  lockedPrimaryRowGroupColumnIds?: string[]
  allPossibleLockedPrimaryRowGroupColumnIds?: string[]
  forceLockedPrimaryRowGroupColumns?: boolean
  editableRowGroupColumnConfig?: Record<string, RowDataGetter<any>>
  showLoader?: boolean

  shouldStopEditOnRowDataUpdate?: (previousRowData: any, newRowData: any) => boolean

  canEditRow?: (rowItem: any) => boolean
  onSelectionChanged?: (rowIds: string[]) => unknown
  onRowEditingStarted?: (rowId: string, rowItem: any) => unknown
  onRowEditingStopped?: (rowId: string, rowItem: any) => unknown
  onClose?: () => unknown
  onCellClicked?: (colId: string, rowIndex: number, rowItem: any, gridApi: GridApi) => unknown
  onCellValueChanged?: (rowId: string, rowItem: any, changedField: string) => unknown
  onCellValueUpdated?: (
    rowId: string,
    rowItem: any,
    updatedField: string,
    newValue: string,
    rawValue?: string,
    gridApi?: GridApi
  ) => unknown
  shouldRedrawOnCellValueUpdate?: (field: string, rowItem: any) => boolean
  onRowGroupOpened?: (rowItem: any, expanded: boolean) => unknown
  saveExpandedCollapsedRows?: boolean
  handleExport?: (exportCsv: () => unknown) => unknown
  exportFileName?: string
  onAfterRestoreDefaultLayout?: (gridApi: GridApi) => unknown
  onPageChanged?: (pageNumber: number) => unknown
  onCellEditingStarted?: (evt: CellEditingStartedEvent) => unknown
  onCellEditingStopped?: (evt: CellEditingStoppedEvent) => unknown
  onModelUpdated?: (evt: ModelUpdatedEvent) => unknown
}

interface ConnectedProps {
  bestPriceType: BestPriceType
  gridSettings: GridSettings
  gridColumnsAutoSized: boolean
  userPreference: UserPreference
  gridRowIdInEdit: string | null
  gridRowInEdit: any
  setCurrentGridId: (gridId: GridId | null) => unknown
  setGridRowInEdit: (rowId: string | null, rowData: any) => unknown
  onSetColumnsAutoSized: (gridId: GridId, autoSized: boolean) => unknown
  onSaveGridColumnSettings: (gridId: GridId, gridSettingsId: GridSettingsId, columnSettings: GridColumnSettings) => unknown
  onSaveCollapsedRowKeys: (gridId: GridId, gridSettingsId: GridSettingsId, collapsedRowKeys: string[]) => unknown
  onSaveSelectedItemIds: (gridId: GridId, gridSettingsId: GridSettingsId, selectedItemIds: string[]) => unknown
}

export type DataGridProps<D> = OwnProps<D> & ConnectedProps

interface DataGridState {
  gridApi: GridApi | null
  isInitialised: boolean
  searchValue: string | null
  toolbarSettings: GridToolbarSettings
}

interface ColumnSettingsChanged {
  readonly columnState?: ReadonlyArray<ColumnState>
}

export enum GridAutoSizing {
  ALL = 'ALL',
  IGNORE_HEADERS = 'IGNORE_HEADERS',
  NONE = 'NONE'
}

const DataGridContainer = styled.div`
  flex: 1 1 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  max-width: 100%;
`

const gridIdSelector = <D extends any>(_state: {}, ownProps: OwnProps<D>) => ownProps.gridId

const mapStateToProps = createStructuredSelector({
  bestPriceType: bestPriceTypeSelector,
  gridSettings: createSelectedGridSettingsSelector(gridIdSelector, () => 'default'),
  gridColumnsAutoSized: createGridColumnAutoSizedSelector(gridIdSelector),
  userPreference: userPreferenceSelector,
  gridRowIdInEdit: gridRowIdInEditSelector,
  gridRowInEdit: gridRowInEditSelector
})

const mapDispatchToProps = {
  setCurrentGridId: SelectionActions.setCurrentGridId,
  setGridRowInEdit: SelectionActions.setGridRowInEdit,
  onSetColumnsAutoSized: SelectionActions.setColumnsAutoSized,
  onSaveGridColumnSettings: GridActions.saveGridColumnSettings,
  onSaveCollapsedRowKeys: GridActions.saveCollapsedRowKeys,
  onSaveSelectedItemIds: GridActions.saveSelectedItemIds
}

export class DataGridComponent<D = any> extends React.Component<DataGridProps<D>, DataGridState> {
  state: DataGridState = {
    gridApi: null,
    isInitialised: false,
    searchValue: null,
    toolbarSettings: {
      showFilter: false,
      showGrouping: false,
      showColumnSelector: false,
      showDelta: false,
      showCopySelected: false
    }
  }

  resizeTimerId: number | undefined = undefined
  previousLockedPrimaryRowGroupColumnIds: string[] | undefined = undefined

  clipboardProcessor =
    this.props.clipboardProcessor ?? new DefaultClipboardProcessor({ copyPasteFormat: this.props.userPreference.copyPasteFormat })

  visibleLeafNodes: IRowNode[] = []

  render() {
    const {
      gridId,
      gridSettings,
      'data-test-id': dataTestId,
      disableGrouping,
      disableFilters,
      disableExport,
      groupRowData,
      bestPriceType,
      subscriptionFilters,
      subscriptionDetail,
      onPageChanged,
      onClose
    } = this.props
    const { searchValue, toolbarSettings, gridApi } = this.state

    const columns = gridApi ? gridApi.getColumns() : []

    const SubscriptionFilters = gridApi ? subscriptionFilters?.(gridApi) : null

    return (
      <DataGridContainer data-test-id={dataTestId}>
        <DataGridToolbarContainer onPaste={stopPastePropagation}>
          {SubscriptionFilters}
          <DataGridToolbar
            disableGrouping={disableGrouping}
            disableFilters={disableFilters}
            disableExport={disableExport}
            toolbarSettings={toolbarSettings}
            searchValue={searchValue}
            renderGroupByToggles={() => this.renderGroupByToggles()}
            renderCustomToolbarButtons={() => this.renderCustomToolbarButtons()}
            renderCustomToolbarComponents={() => this.renderCustomToolbarComponents()}
            renderCustomToolbarSwitches={this.renderCustomToolbarSwitches}
            onSearchUpdated={this.handleSearchUpdated}
            onToolbarSettingsChanged={this.handleToolbarSettingsChanged}
            onExportCsv={this.handleExport}
            onRestoreDefaultLayout={this.handleRestoreDefaultLayout}
            onClose={onClose}
            onAutoFitColumns={this.autoSizeColumns}
          />
        </DataGridToolbarContainer>
        <DataGridContext.Provider value={{ groupRowData }}>
          <AgGridWrapper
            context={{
              userPreference: this.props.userPreference,
              groupRowData,
              bestPriceType,
              gridId
            }}
            getContextMenuItems={this.props.getContextMenuItems}
            applyTransactionGetId={this.props.applyTransactionGetId}
            debug={this.props.debug}
            rowData={this.props.rowData}
            columnDefs={this.props.columnDefs}
            columnState={gridSettings.columnSettings.columnState}
            gridOptions={this.getGridOptions()}
            className={this.props.gridClass}
            placeholder={this.props.placeholder}
            lockedPrimaryRowGroupColumnIds={this.props.lockedPrimaryRowGroupColumnIds}
            editableRowGroupColumnConfig={this.props.editableRowGroupColumnConfig}
            canEditRow={this.props.canEditRow}
            showLoader={this.props.showLoader}
            showFilter={toolbarSettings.showFilter}
            showGrouping={toolbarSettings.showGrouping}
            groupDefaultExpanded={this.props.groupDefaultExpanded}
            defaultGroupSortComparator={this.props.defaultGroupSortComparator}
            onGridReady={this.handleGridReady}
            onFirstRowDataUpdate={this.handleFirstRowDataUpdate}
            onRowDataUpdate={this.handleRowDataUpdate}
            onSelectionChanged={this.props.onSelectionChanged}
            onRowEditingStarted={this.handleRowEditingStarted}
            onRowEditingStopped={this.handleRowEditingStopped}
            onCellEditingStarted={this.props.onCellEditingStarted}
            onCellEditingStopped={this.props.onCellEditingStopped}
            onCellClicked={this.props.onCellClicked}
            onCellValueChanged={this.props.onCellValueChanged}
            onCellValueUpdated={this.handleCellValueUpdated}
            onRowGroupOpened={this.handleRowGroupOpened}
            onProcessCellForClipboard={this.clipboardProcessor.processCell}
            onSendToClipboard={this.clipboardProcessor.sendToClipboard}
            onColumnStateChanged={this.handleColumnStateChangedAndSetState}
            onColumnRowGroupChangeRequest={this.handleColumnRowGroupChangeRequest}
            onModelUpdated={this.handleModelUpdated}
            onSaveSelectedItemIds={this.handleSaveSelectedItemIds}
          />
        </DataGridContext.Provider>
        {onPageChanged ? <PageControlsToolbar subscriptionDetail={subscriptionDetail} onPageChanged={onPageChanged} /> : null}
        <ColumnSelector
          columns={columns}
          gridApi={this.state.gridApi}
          toolbarSettings={toolbarSettings}
          onToolbarSettingsChanged={this.handleToolbarSettingsChanged}
        />
      </DataGridContainer>
    )
  }

  componentDidMount() {
    this.props.setCurrentGridId(this.props.gridId)
  }

  componentDidUpdate(prevProps: DataGridProps<D>) {
    const { gridApi } = this.state
    const {
      userPreference,
      lockedPrimaryRowGroupColumnIds,
      disableGrouping,
      bestPriceType,
      allPossibleLockedPrimaryRowGroupColumnIds,
      forceLockedPrimaryRowGroupColumns,
      columnDefs
    } = this.props

    if (!gridApi) {
      return
    }

    // Redraw the grid to apply new user preferences
    if (prevProps.userPreference !== userPreference) {
      gridApi.refreshCells()
      gridApi.refreshHeader()
      gridApi.redrawRows()
    }

    if (prevProps.userPreference.copyPasteFormat !== userPreference.copyPasteFormat) {
      this.clipboardProcessor.setCopyPasteFormat(userPreference.copyPasteFormat)
    }

    if (prevProps.columnDefs !== columnDefs) {
      setTimeout(() => {
        // redraw in next tick when the column definitions have changed.
        gridApi.refreshHeader()
        gridApi.redrawRows()
      }, 0)
    }

    if (prevProps.bestPriceType !== bestPriceType) {
      gridApi.redrawRows()
    }

    if (gridApi) {
      if (lockedPrimaryRowGroupColumnIds && lockedPrimaryRowGroupColumnIds.length > 0) {
        if (!isEqual(lockedPrimaryRowGroupColumnIds, this.previousLockedPrimaryRowGroupColumnIds)) {
          if (forceLockedPrimaryRowGroupColumns) {
            gridApi.setRowGroupColumns(lockedPrimaryRowGroupColumnIds)
          } else {
            const currentRowGroupIds = gridApi.getRowGroupColumns().map(rowGroupColumn => rowGroupColumn.getColId())

            if (currentRowGroupIds.length === 0) {
              gridApi.addRowGroupColumns(lockedPrimaryRowGroupColumnIds)
            } else if (
              currentRowGroupIds.length < lockedPrimaryRowGroupColumnIds.length ||
              currentRowGroupIds.some((rowGroupId, idx) => rowGroupId !== lockedPrimaryRowGroupColumnIds[idx])
            ) {
              /*
              The following is to cater for the scenario...

              1. Grid is rendered with a previous locked primary row group column, e.g. 'negotiation_order_id' and an additional
                 user defined grouping, eg 'status'.
              2. User navigates away, and adds a negotiation that includes a collection, this will alter the locked primary
                 row group columns to be 'collection_name', 'counterparty_name'
              3. Grid renders, but the currentRowGroupIds is persisted, so still contains 'negotiation_order_id' and 'status'
                 groupings from previous rendering. Therefore resultant grouping would be 'collection_name', 'counterparty_name',
                 'negotiation_order_id', 'status'

              Its necessary therefore to filter out those additional groupings in currentRowGroupIds that may have been set via a
              previous lockedPrimaryRowGroupColumnIds, but maintain the user defined grouping 'status'.
               */
              const newRowGroupIds = uniq(
                lockedPrimaryRowGroupColumnIds.concat(
                  currentRowGroupIds.filter(rowGroupId => {
                    return !allPossibleLockedPrimaryRowGroupColumnIds?.includes(rowGroupId)
                  })
                )
              )

              gridApi.removeRowGroupColumns(currentRowGroupIds)
              gridApi.addRowGroupColumns(newRowGroupIds)
            }
          }

          this.previousLockedPrimaryRowGroupColumnIds = this.props.lockedPrimaryRowGroupColumnIds
        }
      } else if (this.previousLockedPrimaryRowGroupColumnIds) {
        gridApi.removeRowGroupColumns(this.previousLockedPrimaryRowGroupColumnIds)
        this.previousLockedPrimaryRowGroupColumnIds = undefined
      }
    }

    const groupingHasChanged = prevProps.disableGrouping !== disableGrouping
    const colDefsHaveChanged = this.props.columnDefs !== prevProps.columnDefs

    if (groupingHasChanged) {
      this.restoreDefaultLayout()
    }

    if (colDefsHaveChanged && disableGrouping) {
      this.resetColumnsAndRestore(this.props.columnDefs)
    }
  }

  componentWillUnmount() {
    this.handleColumnStateChangedDebounced.flush()
    if (this.state.gridApi) {
      this.state.gridApi.setGridOption('quickFilterText', '')
    }
    if (this.props.trackGridRowInEdit) {
      this.props.setGridRowInEdit(null, null)
    }

    if (this.resizeTimerId) {
      window.clearTimeout(this.resizeTimerId)
    }
  }

  renderGroupByToggles = () => {
    const { isInitialised, gridApi } = this.state
    const { GroupByToggles } = this.props
    if (GroupByToggles && isInitialised) {
      return <GroupByToggles gridApi={gridApi} rowData={this.props.rowData} />
    }
    return null
  }

  renderCustomToolbarButtons = () => {
    const { customToolbarButtons } = this.props
    const { toolbarSettings, gridApi } = this.state

    return customToolbarButtons ? (
      <>
        {customToolbarButtons.map((CustomToolbarButton, index) => (
          <CustomToolbarButton
            key={index}
            gridApi={gridApi}
            toolbarSettings={toolbarSettings}
            onToolbarSettingsChanged={this.handleToolbarSettingsChanged}
          />
        ))}
      </>
    ) : null
  }

  renderCustomToolbarComponents = () => {
    const { customToolbarComponents, gridId } = this.props
    const { gridApi, toolbarSettings } = this.state

    return customToolbarComponents ? (
      <>
        {customToolbarComponents.map((CustomToolbarComponent, index) => (
          <CustomToolbarComponent
            key={index}
            gridId={gridId}
            gridApi={gridApi}
            toolbarSettings={toolbarSettings}
            onToolbarSettingsChanged={this.handleToolbarSettingsChanged}
          />
        ))}
      </>
    ) : null
  }

  renderCustomToolbarSwitches = () => {
    const { customToolbarSwitches } = this.props

    return customToolbarSwitches ? (
      <>
        {customToolbarSwitches.map((CustomToolbarSwitch, index) => (
          <CustomToolbarSwitch key={index} />
        ))}
      </>
    ) : null
  }

  private getGridOptions = () => {
    const { saveExpandedCollapsedRows, gridSettings, gridOptions } = this.props

    if (!saveExpandedCollapsedRows) {
      return gridOptions
    }

    return {
      ...gridOptions,
      isGroupOpenByDefault: (params: IsGroupOpenByDefaultParams) => {
        if (!gridSettings.collapsedRowKeys) {
          return true
        }
        return !gridSettings.collapsedRowKeys.includes(params.key)
      }
    }
  }

  private handleSearchUpdated = (value: string) => {
    this.setState({ searchValue: value }, () => {
      if (this.state.gridApi) {
        this.state.gridApi.setGridOption('quickFilterText', value)
      }
    })
  }

  private handleToolbarSettingsChanged = (toolbarSettings: GridToolbarSettings) => {
    this.setState({ toolbarSettings })
  }

  private handleColumnSettingsChanged = ({ columnState }: ColumnSettingsChanged) => {
    const { gridId, onSaveGridColumnSettings } = this.props

    if (onSaveGridColumnSettings) {
      const columnSettings: GridColumnSettings = {
        viewId: 'default',
        ...(columnState && columnState.length > 0 && { columnState })
      }

      onSaveGridColumnSettings(gridId, columnSettings.viewId, columnSettings)
    }
  }

  private handleExport = () => {
    if (this.props.handleExport) {
      this.props.handleExport(this.handleExportCsv)
    } else {
      this.handleExportCsv()
    }
  }

  private handleExportCsv = () => {
    if (this.state.gridApi) {
      const isGrouped = this.state.gridApi.getRowGroupColumns().length > 0
      const hasSelectedRows = isNotEmpty(this.state.gridApi.getSelectedRows())
      const columnKeys = this.state.gridApi
        .getColumns()
        ?.map(col => col.getColId())
        .filter(
          colId =>
            !(
              colId === StandardGridColumnId.CHECKBOX ||
              colId === StandardGridColumnId.ACTIONS ||
              colId === StandardGridColumnId.MASTER_DETAIL ||
              (colId === StandardGridColumnId.GROUPING && !isGrouped)
            )
        )

      if (columnKeys) {
        this.state.gridApi.exportDataAsCsv({
          columnKeys,
          onlySelected: hasSelectedRows,
          fileName: this.props.exportFileName,
          processHeaderCallback: params => {
            const colDef = params.column.getColDef()
            switch (colDef.headerName) {
              case Locale.translate(Translation.TC_COUNTERPARTY):
                return DefaultHeading.CounterpartyName
              case Locale.translate(Translation.TC_ABBREVIATED_DIRECTION):
                return DefaultHeading.Direction
              default:
                return colDef.headerName ?? ''
            }
          }
        })
      }
    }
  }

  private resetColumnsAndRestore = (newColumnDefs: (ColDef | ColGroupDef)[]) => {
    const { gridApi } = this.state

    if (gridApi) {
      const prevColumnState: readonly ColumnState[] | undefined = this.props.gridSettings.columnSettings.columnState

      gridApi.setGridOption('columnDefs', newColumnDefs)
      gridApi.resetColumnState()

      if (!Array.isArray(prevColumnState)) {
        return
      }
      const flattenCols = flattenColumnDefs(newColumnDefs)

      if (flattenCols.length >= prevColumnState.length) {
        prevColumnState.forEach(colState => {
          colState.colId && gridApi.setColumnsVisible([colState.colId], !colState.hide)
        })
      }
    }
  }

  private restoreDefaultLayout = () => {
    const { gridApi } = this.state
    const { gridId, columnDefs, onSetColumnsAutoSized, onAfterRestoreDefaultLayout } = this.props

    const flattenedColumnDefs = flattenColumnDefs(columnDefs)
    const defaultSortModel = flattenedColumnDefs.filter(colDefWithIdAndSort).map(colDef => ({
      colId: colDef.colId,
      sort: colDef.sort
    }))

    if (gridApi) {
      gridApi.resetColumnState()
      gridApi.resetColumnGroupState()
      gridApi.applyColumnState({ state: defaultSortModel, defaultState: { sort: null } })
      gridApi.setFilterModel(null)
      this.previousLockedPrimaryRowGroupColumnIds = undefined

      this.setState(() => ({
        toolbarSettings: {
          showFilter: false,
          showGrouping: false,
          showColumnSelector: false,
          showDelta: false,
          showCopySelected: false
        }
      }))

      onAfterRestoreDefaultLayout?.(gridApi)

      // If there are rows displayed, auto-size the columns, otherwise set columnsAutoSized for the
      // grid to false so the grid  auto-sizes the columns on the next data update
      if (gridApi.getDisplayedRowCount() > 0) {
        this.autoSizeColumns()
      } else {
        onSetColumnsAutoSized(gridId, false)
      }
    }
  }

  private handleRestoreDefaultLayout = async () => {
    const { showDialog } = this.props

    const dialogArgs = {
      type: NotificationType.Confirm,
      title: Translation.TC_RESTORE_DEFAULT_LAYOUT,
      message: Translation.SC_RESTORE_DEFAULT_LAYOUT_CONFIRMATION,
      buttons: [Translation.TC_BUTTON_NO, Translation.TC_BUTTON_YES],
      cancelButton: Translation.TC_BUTTON_NO,
      defaultButton: Translation.TC_BUTTON_YES
    }
    const dialogResult = await showDialog(dialogArgs)

    if (dialogResult === Translation.TC_BUTTON_YES) {
      this.restoreDefaultLayout()
    }
  }

  private autoSizeColumns = () => {
    const { gridApi } = this.state
    const { gridId, onSetColumnsAutoSized, gridColumnAutoSizing } = this.props

    if (isNil(gridApi) || gridColumnAutoSizing === GridAutoSizing.NONE) {
      return
    }

    // ag-grid appears to render the actions button asynchronously so calling autoSizeAllColumns too soon
    // results in an incorrect column width.
    this.resizeTimerId = window.setTimeout(() => {
      const shouldIgnoreHeaders = !isNil(gridColumnAutoSizing) && gridColumnAutoSizing === GridAutoSizing.IGNORE_HEADERS
      gridApi && !gridApi.isDestroyed() && gridApi.autoSizeAllColumns(shouldIgnoreHeaders)
    }, 400)

    onSetColumnsAutoSized(gridId, true)
  }

  private handleGridReady = (gridApi: GridApi) => {
    const { debug, gridApiRef } = this.props
    if (gridApiRef) {
      gridApiRef.current = gridApi
    }

    debug && gridEventsLogger(gridApi)

    this.setState({ gridApi, isInitialised: true })
  }

  // Auto-size columns if data was added into an empty grid for the first time in a session
  private handleFirstRowDataUpdate = () => {
    const {
      state: { gridApi },
      props: { gridColumnsAutoSized }
    } = this

    if (isNil(gridApi) || gridColumnsAutoSized) {
      return
    }

    this.autoSizeColumns()
  }

  private handleRowDataUpdate = () => {
    const { gridRowIdInEdit, gridRowInEdit, trackGridRowInEdit, shouldStopEditOnRowDataUpdate, setGridRowInEdit } = this.props
    const { gridApi } = this.state

    if (isNil(gridRowIdInEdit) || !gridApi || !trackGridRowInEdit) {
      return
    }
    // Update grid row in edit
    const newRowData = gridApi.getRowNode(gridRowIdInEdit)?.data
    if (shouldStopEditOnRowDataUpdate?.(gridRowInEdit, newRowData)) {
      gridApi.stopEditing()
    } else {
      setGridRowInEdit(gridRowIdInEdit, newRowData)
    }
  }

  private handleModelUpdated = (e: ModelUpdatedEvent) => {
    const { onModelUpdated } = this.props
    const filteredLeafNodes = getAllVisibleLeafNodes(e.api)
    setNodesSelected(
      this.visibleLeafNodes.filter(n => !filteredLeafNodes.includes(n)),
      false
    )
    this.visibleLeafNodes = [...filteredLeafNodes]

    if (onModelUpdated) {
      onModelUpdated(e)
    }
  }

  private handleRowEditingStarted = (rowId: string, rowItem: any) => {
    const { trackGridRowInEdit, setGridRowInEdit, onRowEditingStarted } = this.props

    if (trackGridRowInEdit) {
      setGridRowInEdit(rowId, rowItem)
    }

    if (onRowEditingStarted) {
      onRowEditingStarted(rowId, rowItem)
    }
  }

  private handleRowEditingStopped = (rowId: string, rowItem: any) => {
    const { trackGridRowInEdit, setGridRowInEdit, onRowEditingStopped } = this.props

    if (trackGridRowInEdit) {
      setGridRowInEdit(null, null)
    }

    if (onRowEditingStopped) {
      onRowEditingStopped(rowId, rowItem)
    }
  }

  // TODO - TL think of a better way to update editability of columns after isin/basket change
  private restartEditing = debounce((rowId: string, rowItem: any, field: string) => {
    const { gridApi } = this.state

    const rowIndexString = (gridApi?.getRowNode(rowId) as RowNode)?.getRowIndexString()
    const colKey = gridApi
      ?.getColumns()
      ?.find(column => {
        const columnUpdateField = column.getColDef()?.cellEditorParams?.updateField
        if (!columnUpdateField) {
          return false
        }
        const updateField = typeof columnUpdateField === 'string' ? columnUpdateField : columnUpdateField?.(rowItem)
        return updateField === field
      })
      ?.getColId()
    if (rowIndexString && colKey) {
      const rowIndex = parseInt(rowIndexString, 10)

      gridApi?.stopEditing()
      gridApi?.startEditingCell({ rowIndex, colKey })
    }
  }, 250)

  private handleCellValueUpdated = (rowId: string, rowItem: any, field: string, newValue: any, rawValue?: any) => {
    const { onCellValueUpdated, shouldRedrawOnCellValueUpdate } = this.props

    onCellValueUpdated?.(rowId, rowItem, field, newValue, rawValue, this.state.gridApi || undefined)

    if (shouldRedrawOnCellValueUpdate?.(field, rowItem)) {
      this.restartEditing(rowId, rowItem, field)
    }
  }

  private handleRowGroupOpened = (expanded: boolean, all: boolean, rowItem?: any) => {
    const { onSaveCollapsedRowKeys, saveExpandedCollapsedRows, gridId, onRowGroupOpened } = this.props
    const { gridApi } = this.state

    if (onRowGroupOpened) {
      onRowGroupOpened(rowItem, expanded)
    }

    if (!saveExpandedCollapsedRows) {
      return
    }

    if (all && expanded) {
      onSaveCollapsedRowKeys(gridId, 'default', [])
      return
    }

    const collapsedGroupRowNodeKeys =
      gridApi
        ?.getRenderedNodes()
        // Only persist collapsed state if its a top level group
        .filter(node => node.group && !node.expanded && node.level === 0)
        .map(node => node.key)
        .filter((key): key is string => !!key) ?? []

    onSaveCollapsedRowKeys(gridId, 'default', collapsedGroupRowNodeKeys)
  }

  private handleColumnStateChangedAndSetState = (columnState: ColumnState[]) => {
    this.forceUpdate()
    this.handleColumnStateChangedDebounced(columnState)
  }

  private handleColumnStateChangedDebounced = debounce(
    (columnState: ColumnState[]) => this.handleColumnSettingsChanged({ columnState }),
    750
  )

  // TODO handle multiple enforced grouping columns
  // Check if the current grouping column is one of the enforced column groupings and return the column
  private getGroupingColStateEnforced = (rowGroupColumns: Column[]) => {
    const { enforcedGroupingColStates } = this.props
    if (rowGroupColumns.length === 1) {
      const groupingColId = rowGroupColumns[0].getColId()
      return enforcedGroupingColStates?.find(colState => groupingColId === colState.colId) ?? null
    } else {
      return null
    }
  }

  private handleColumnRowGroupChangeRequest = (newColumns: Column[], prevColumns: Column[] | null) => {
    const { gridApi } = this.state
    const newGroupingColStateEnforced = this.getGroupingColStateEnforced(newColumns)
    const prevGroupingColStateEnforced = prevColumns ? this.getGroupingColStateEnforced(prevColumns) : null

    const prevSortModel = gridApi?.getColumnState()

    if (newGroupingColStateEnforced) {
      // If the new column grouping is one of the enforced groupings, set sort model
      gridApi?.applyColumnState({ state: [{ colId: newGroupingColStateEnforced.colId, sort: newGroupingColStateEnforced.sort }] })
    } else if (
      prevGroupingColStateEnforced &&
      prevSortModel?.length === 1 &&
      prevSortModel[0].colId === prevGroupingColStateEnforced?.colId &&
      prevSortModel[0].sort === prevGroupingColStateEnforced?.sort
    ) {
      // If the new column grouping is not one of the enforced groupings and the previous grouping is
      // one of the enforced groupings, set sort model to none
      gridApi?.applyColumnState({ defaultState: { sort: null } })
    }
    // if the new and previous column grouping are both not one of the enforced groupings, keep current sort model
  }

  private handleSaveSelectedItemIds = () => {
    const { gridApi } = this.state
    const { saveSelectedItemIds, onSaveSelectedItemIds, gridId } = this.props
    const selectedRows = gridApi?.getSelectedRows()

    if (saveSelectedItemIds && selectedRows) {
      const Ids = selectedRows.map(selectedRow => selectedRow.deal.dealId)
      onSaveSelectedItemIds(gridId, 'default', Ids)
    }
  }
}

const ConnectedDataGrid = connect(mapStateToProps, mapDispatchToProps)(withModal(DataGridComponent))

export const DataGrid = <D extends any>(props: Omit<OwnProps<D>, 'showModal' | 'showDialog'>) => <ConnectedDataGrid {...props} />
